/**
 * Tenants Page - Phase D Enhanced
 * Modern SaaS Control Plane tenant list with comprehensive filtering and status indicators
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { PermissionGate } from '../components';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import {
  Building2,
  Search,
  Plus,
  Loader2,
  MoreHorizontal,
  Eye,
  LogIn,
  Pause,
  Play,
  Trash2,
  Boxes,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  AlertTriangle,
  Download,
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { describeEnvironment } from './environmentPresentation';
import StatusBadge from '../../../components/admin/StatusBadge';
import EmptyState from '../../../components/admin/EmptyState';
import { Skeleton } from '../../../components/ui/skeleton';
import { canSuspend, canReactivate, canTerminate, isTerminal } from './tenantLifecycle';
import { describeProgressCompact } from './provisioningProgress';
import ImpersonationReasonDialog from './ImpersonationReasonDialog';
import {
  ConfirmTerminateDialog, refusalText, terminateUnavailableText,
} from './TerminateTenantControl';
import TenantMark from '../../../design-system/identity/TenantMark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

const toCsvValue = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

const exportTenantsCsv = (tenants) => {
  const cols = ['tenant_name', 'company_name', 'subdomain', 'status', 'plan', 'current_users', 'seat_limit', 'billing_status', 'created_at'];
  const header = cols.join(',');
  const rows = tenants.map((t) => cols.map((c) => toCsvValue(t[c])).join(','));
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tenants.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const TenantsPage = () => {
  const { getAdminToken, adminUser } = useAdminAuth();
  // Delete (a soft delete: terminate + hide) is Owner only and needs the
  // tenant's name and a reason (D3 hotfix, Option B). The dialog and the role
  // rule are TerminateTenantControl's own, reused rather than copied.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteReturnFocus = useRef(null);
  const headingRef = useRef(null);
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [billingFilter, setBillingFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'suspend'|'activate', tenant }
  // Open Workspace. Non-null opens the reason dialog; the mint and the redirect
  // are startViewAs's, behind it. This page never builds a CRM URL.
  const [viewAsTarget, setViewAsTarget] = useState(null);
  const [busyTenantId, setBusyTenantId] = useState(null);
  const limit = 20;

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const params = { 
        skip: page * limit, 
        limit,
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        plan: planFilter !== 'all' ? planFilter : undefined,
        billing_status: billingFilter !== 'all' ? billingFilter : undefined,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      const response = await axios.get(`${ADMIN_API}/tenants`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
        params
      });
      setTenants(response.data.tenants || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when a filter/sort/page value changes
  }, [page, statusFilter, planFilter, billingFilter, sortBy, sortOrder]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setPage(0);
      fetchTenants();
    }, 300);
    return () => clearTimeout(delaySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the search query changes
  }, [searchQuery]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const openConfirm = (type, tenant, e) => {
    e.stopPropagation();
    setConfirmAction({ type, tenant });
  };

  const openDelete = (tenant, e) => {
    e.stopPropagation();
    // Focus goes back to this row's actions button when the dialog closes.
    deleteReturnFocus.current = e.currentTarget.ownerDocument
      .querySelector(`[data-row-actions="${tenant.id}"]`);
    setDeleteTarget(tenant);
  };

  // A refusal is RE-THROWN: the dialog shows it beside the field it names.
  const deleteTenant = async ({ confirmTenantName, reason }) => {
    const tenant = deleteTarget;
    setBusyTenantId(tenant.id);
    try {
      const res = await axios.delete(`${ADMIN_API}/tenants/${tenant.id}`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
        data: { confirm_tenant_name: confirmTenantName, reason },
      });
      if (res && res.status === 202) {
        toast(refusalText({ response: res }, 'Deleted; a repair step is pending.'), { duration: 15000 });
      } else {
        toast.success(`${tenant.tenant_name || tenant.company_name} deleted`);
      }
      // The row is about to leave the list, so focus lands on the heading.
      deleteReturnFocus.current = headingRef.current;
      fetchTenants();
    } finally {
      setBusyTenantId(null);
    }
  };

  const openWorkspace = (tenant, e) => {
    e.stopPropagation();
    setViewAsTarget({ tenantId: tenant.id, tenantName: tenant.tenant_name || tenant.company_name });
  };

  const postSupportAction = (path, body) => axios.post(
    `${ADMIN_API}/control-plane${path}`, body,
    { headers: { Authorization: `Bearer ${getAdminToken()}` } },
  );

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, tenant } = confirmAction;
    setBusyTenantId(tenant.id);
    try {
      if (type === 'suspend') {
        await axios.post(`${ADMIN_API}/control-plane/tenants/${tenant.id}/support/action`,
          { action: 'SUSPEND', reason: 'Admin action' },
          { headers: { Authorization: `Bearer ${getAdminToken()}` } }
        );
        toast.success(`${tenant.tenant_name || tenant.company_name} suspended`);
      } else if (type === 'activate') {
        await axios.post(`${ADMIN_API}/control-plane/tenants/${tenant.id}/support/action`,
          { action: 'REACTIVATE', reason: 'Admin action' },
          { headers: { Authorization: `Bearer ${getAdminToken()}` } }
        );
        toast.success(`${tenant.tenant_name || tenant.company_name} activated`);
      }
      setConfirmAction(null);
      fetchTenants();
    } catch (error) {
      console.error(`Failed to ${type} tenant:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${type} tenant`);
    } finally {
      setBusyTenantId(null);
    }
  };

  const getStatusBadge = (status) => <StatusBadge status={status || 'unknown'} />;

  const getPlanBadge = (plan) =>
    plan ? (
      <Badge variant="outline" className="border-primary/20 bg-primary/5 font-medium capitalize text-primary">
        {plan}
      </Badge>
    ) : (
      // There is no Free plan in Cluvik, and after the commercial reset most
      // tenants have none at all — so this label was the common case, not an
      // edge, and operators make commercial decisions from this list.
      <Badge variant="outline" className="border-border bg-muted font-medium text-muted-foreground">
        No plan
      </Badge>
    );

  const getBillingBadge = (billingStatus) => <StatusBadge status={billingStatus || 'current'} />;

  const totalPages = Math.ceil(total / limit);

  // Calculate stats from current data
  const stats = useMemo(() => {
    return {
      active: tenants.filter(t => (t.status || '').toLowerCase() === 'active').length,
      suspended: tenants.filter(t => (t.status || '').toLowerCase() === 'suspended').length,
      // Trial-ness is tracked via the is_trial flag, not the status field —
      // no tenant ever has status === 'trial' (TenantStatus has no such value).
      trial: tenants.filter(t => t.is_trial).length
    };
  }, [tenants]);

  const SortableHeader = ({ column, children }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted transition-colors"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column ? (
          sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
        ) : (
          <ArrowUpDown className="h-4 w-4 text-input" />
        )}
      </div>
    </TableHead>
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between animate-fade-in-up">
        <div>
          <h1 ref={headingRef} tabIndex={-1}
              className="text-2xl font-semibold tracking-tight text-foreground focus:outline-none">
            Tenant Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage organizations, subscriptions, and access controls
          </p>
        </div>
        {/* Wraps: at 320 the unwrapped row was 371px wide and pushed Create
            Tenant, this journey's primary action, off the edge (T29, S18). */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchTenants} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportTenantsCsv(tenants)} disabled={!tenants.length} className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
          <PermissionGate permission="tenant:create">
            <Button
              onClick={() => navigate('/tenants/create')}
              data-testid="create-tenant-btn"
              className="gap-2 bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Create Tenant
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Quick Stats — a wrapping row, not a three-column grid: three
          columns at 375 wide is under 110px a tile, and "Suspended" with its
          count does not fit in one. */}
      <div className="flex flex-wrap gap-3" data-testid="tenant-quick-stats">
        <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2">
          <span className="h-2 w-2 rounded-full bg-success" />
          <span className="text-sm text-foreground"><span className="font-semibold">{stats.active}</span> Active</span>
        </div>
        <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2">
          <span className="h-2 w-2 rounded-full bg-destructive" />
          <span className="text-sm text-foreground"><span className="font-semibold">{stats.suspended}</span> Suspended</span>
        </div>
        <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2">
          <span className="h-2 w-2 rounded-full bg-info" />
          <span className="text-sm text-foreground"><span className="font-semibold">{stats.trial}</span> Trial</span>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* min-w capped at the column: 250px was wider than the card's
                content box at 320 (about 238px). */}
            <div className="relative flex-1 min-w-[min(250px,100%)] max-w-md">
              <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-input" />
              <Input
                aria-label="Search tenants"
                placeholder="Search by company, subdomain, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="tenant-search-input"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="SUSPENDED">Suspended</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="READ_ONLY">Read Only</SelectItem>
                <SelectItem value="TERMINATED">Terminated</SelectItem>
              </SelectContent>
            </Select>

            <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]" data-testid="plan-filter">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>

            <Select value={billingFilter} onValueChange={(v) => { setBillingFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]" data-testid="billing-filter">
                <SelectValue placeholder="Billing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Billing</SelectItem>
                <SelectItem value="CURRENT">Current</SelectItem>
                <SelectItem value="OVERDUE">Overdue</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
              </SelectContent>
            </Select>

            {(statusFilter !== 'all' || planFilter !== 'all' || billingFilter !== 'all' || searchQuery) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setPlanFilter('all');
                  setBillingFilter('all');
                  setSearchQuery('');
                  setPage(0);
                }}
                className="text-muted-foreground"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardHeader className="pb-3 border-b">
          <CardTitle className="text-base font-semibold flex items-center justify-between">
            <span>All Tenants ({total})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 py-2">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <Skeleton className="h-4 flex-1 max-w-[180px]" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          ) : tenants.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No tenants found"
              description={
                searchQuery || statusFilter !== 'all' || planFilter !== 'all'
                  ? 'Try adjusting your filters.'
                  : 'Create your first tenant to get started.'
              }
              actionLabel="Create Tenant"
              onAction={() => navigate('/tenants/create')}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <SortableHeader column="tenant_name">Company</SortableHeader>
                    <TableHead>Environment</TableHead>
                    <TableHead>Subdomain</TableHead>
                    <SortableHeader column="status">Status</SortableHeader>
                    <SortableHeader column="plan">Plan</SortableHeader>
                    <TableHead>Seats</TableHead>
                    <TableHead>Modules</TableHead>
                    <TableHead>Storage</TableHead>
                    <TableHead>Trial Expiry</TableHead>
                    <TableHead>Billing</TableHead>
                    <SortableHeader column="updated_at">Last Activity</SortableHeader>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((tenant) => {
                    const usedSeats = tenant.current_users || 0;
                    const totalSeats = tenant.seat_limit || 10;
                    const seatPercent = Math.round((usedSeats / totalSeats) * 100);
                    
                    // null => not measured. Never coerce to 0: that renders a
                    // fabricated "0.0GB used" for every tenant on the fleet.
                    const usedStorageMB = tenant.current_storage_mb;
                    const totalStorageMB = tenant.max_storage_mb || 1024;
                    const storageMeasured = usedStorageMB != null;
                    const storagePercent = storageMeasured
                      ? Math.round((usedStorageMB / totalStorageMB) * 100)
                      : null;
                    
                    const moduleCount = (tenant.module_entitlements || []).length;
                    
                    const isTrialExpiring = tenant.trial_ends_at && 
                      new Date(tenant.trial_ends_at) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

                    return (
                      <TableRow 
                        key={tenant.id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => navigate(`/tenants/${tenant.id}`)}
                        data-testid={`tenant-row-${tenant.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {/* THE CUSTOMER'S MARK. This was an invented
                                `from-indigo-500 to-purple-600` gradient — not
                                on the Cluvik ramp — carrying ONE letter, so
                                every tenant on the fleet wore the same colour
                                and two customers sharing an initial had
                                identical artwork. It is now a neutral Cluvik
                                tile at a ramp position hashed from the tenant
                                ID, which is stable across a rename. See
                                TenantMark.jsx. Decorative: the tenant's name is
                                written immediately beside it. */}
                            <TenantMark
                              tenantId={tenant.id}
                              name={tenant.tenant_name || tenant.company_name}
                              size="lg"
                              className="shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate max-w-[180px]">
                                {tenant.tenant_name || tenant.company_name}
                              </p>
                              <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                                {tenant.organization_name || tenant.industry || 'No industry'}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const env = describeEnvironment(tenant.environment);
                            return (
                              <StatusBadge
                                status={env.code ? env.code.toLowerCase() : 'unclassified'}
                                label={env.code || env.label}
                                dot={false}
                                className="font-semibold tracking-wide"
                                data-testid={`tenant-environment-${tenant.id}`}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-foreground font-mono">
                            {tenant.subdomain || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(tenant.status)}
                          {/* The lifecycle badge keeps its authority — it alone
                              carries SUSPENDED, READ_ONLY and TERMINATED. This
                              adds the compact form of the SAME projection the
                              detail surfaces show, built server-side from the
                              stored job (never a live readiness call per row).
                              Absent on any tenant that is not provisioning, so
                              a finished row carries no stale line. */}
                          {describeProgressCompact(tenant.provisioning_progress) && (
                            <div className="text-xs text-muted-foreground mt-1"
                                 data-testid={`tenant-progress-${tenant.id}`}>
                              {describeProgressCompact(tenant.provisioning_progress)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {getPlanBadge(tenant.plan)}
                        </TableCell>
                        <TableCell>
                          <div className="w-24">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="font-medium">{usedSeats}/{totalSeats}</span>
                              <span className={`${seatPercent >= 90 ? 'text-destructive-text' : 'text-muted-foreground'}`}>
                                {seatPercent}%
                              </span>
                            </div>
                            <Progress 
                              value={seatPercent} 
                              className={`h-1.5 ${seatPercent >= 90 ? '[&>div]:bg-destructive' : ''}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Boxes className="h-3.5 w-3.5 text-input" />
                            <span className="text-sm font-medium">{moduleCount}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="w-20">
                            {storageMeasured ? (
                              <>
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="font-medium">{(usedStorageMB / 1024).toFixed(1)}GB</span>
                                </div>
                                <Progress
                                  value={storagePercent}
                                  className={`h-1.5 ${storagePercent >= 90 ? '[&>div]:bg-destructive' : ''}`}
                                />
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground" title="Storage usage is not measured yet">
                                Not measured
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {tenant.trial_ends_at ? (
                            <div className={`flex items-center gap-1 text-sm ${isTrialExpiring ? 'text-warning-text' : 'text-foreground'}`}>
                              {isTrialExpiring && <AlertTriangle className="h-3.5 w-3.5" />}
                              {new Date(tenant.trial_ends_at).toLocaleDateString()}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {getBillingBadge(tenant.billing_status)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {tenant.updated_at 
                            ? new Date(tenant.updated_at).toLocaleDateString()
                            : tenant.created_at 
                              ? new Date(tenant.created_at).toLocaleDateString()
                              : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              {/* An unnamed "..." read as "button, menu" on every row. */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                disabled={busyTenantId === tenant.id}
                                data-row-actions={tenant.id}
                                aria-label={`Actions for ${tenant.tenant_name || tenant.company_name || tenant.id}`}
                                aria-busy={busyTenantId === tenant.id || undefined}
                              >
                                {busyTenantId === tenant.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <MoreHorizontal className="h-4 w-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/tenants/${tenant.id}`); }}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <PermissionGate permission="tenant:impersonate" tooltip={false}>
                                <DropdownMenuItem onClick={(e) => openWorkspace(tenant, e)} data-testid={`open-workspace-${tenant.id}`}>
                                  <LogIn className="h-4 w-4 mr-2" />
                                  Open Workspace
                                </DropdownMenuItem>
                              </PermissionGate>
                              <DropdownMenuSeparator />
                              {/* Offered only where the backend would allow it.
                                  A terminated tenant gets neither — terminal in
                                  ordinary operations. */}
                              {canSuspend(tenant.status) && (
                                <PermissionGate permission="tenant:suspend" tooltip={false}>
                                  <DropdownMenuItem onClick={(e) => openConfirm('suspend', tenant, e)} className="text-warning-text">
                                    <Pause className="h-4 w-4 mr-2" />
                                    Suspend
                                  </DropdownMenuItem>
                                </PermissionGate>
                              )}
                              {canReactivate(tenant.status) && (
                                <PermissionGate permission="tenant:suspend" tooltip={false}>
                                  <DropdownMenuItem onClick={(e) => openConfirm('activate', tenant, e)} className="text-success">
                                    <Play className="h-4 w-4 mr-2" />
                                    Activate
                                  </DropdownMenuItem>
                                </PermissionGate>
                              )}
                              {isTerminal(tenant.status) && (
                                <DropdownMenuItem disabled className="text-muted-foreground">
                                  Terminated — no lifecycle actions
                                </DropdownMenuItem>
                              )}
                              {/* Delete is a soft terminate, so it is offered at all
                                  only where the lifecycle contract allows one. */}
                              {canTerminate(tenant.status) && (!terminateUnavailableText(
                                adminUser, tenant.tenant_name || tenant.company_name || '') ? (
                                <DropdownMenuItem onClick={(e) => openDelete(tenant, e)}
                                                  className="text-destructive-text"
                                                  data-testid={`delete-tenant-${tenant.id}`}>
                                  <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                                  Delete
                                </DropdownMenuItem>
                              ) : (
                                <>
                                  {/* Disabled WITH the reason: aria-disabled keeps it
                                      reachable in the menu, and the explanation is
                                      its description. Selecting it does nothing. */}
                                  <DropdownMenuItem aria-disabled="true"
                                                    aria-describedby={`delete-unavailable-${tenant.id}`}
                                                    onSelect={(e) => e.preventDefault()}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-muted-foreground opacity-60 cursor-not-allowed"
                                                    data-testid={`delete-tenant-${tenant.id}`}>
                                    <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                                    Delete
                                  </DropdownMenuItem>
                                  <p id={`delete-unavailable-${tenant.id}`} aria-hidden="true"
                                     className="max-w-[16rem] px-2 pb-1.5 text-xs text-muted-foreground">
                                    {terminateUnavailableText(
                                      adminUser, tenant.tenant_name || tenant.company_name || '')}
                                  </p>
                                </>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/50">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total} tenants
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <span className="text-sm text-foreground px-2">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suspend / Activate confirmation (Delete has its own, below) */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'suspend' && 'Suspend Tenant?'}
              {confirmAction?.type === 'activate' && 'Activate Tenant?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'suspend' &&
                `${confirmAction.tenant.tenant_name || confirmAction.tenant.company_name} will lose access immediately. Users cannot log in until reactivated.`}
              {confirmAction?.type === 'activate' &&
                `${confirmAction.tenant.tenant_name || confirmAction.tenant.company_name} will regain access immediately.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runConfirmedAction}
              className={
                /* SOLID FILLS, so each takes its token's own FILL half and
                   the ink that was measured against it — `--destructive` with
                   `--destructive-foreground` at 5.19:1, `--warning` with
                   `--warning-foreground` at 4.78:1. The ink half
                   (`-text`) is for words on a TINT and would be unreadable
                   here; white on `--warning` is 2.44:1 and was what
                   `bg-amber-600` was effectively shipping.

                   AND NO HOVER TINT. A first draft carried a
                   `hover:bg-warning` at ninety per cent and
                   tokenRoles.test.js failed it, correctly: `/90` is a TINT,
                   these `-foreground` inks are measured against the SOLID
                   fill, and a hover state is not exempt from being readable.
                   The ratchet saw a defect a hover state hides from review by
                   only existing under the pointer. */
                confirmAction?.type === 'suspend' ? 'bg-warning text-warning-foreground'
                : confirmAction?.type === 'open_workspace' ? 'bg-primary'
                : ''
              }
            >
              {confirmAction?.type === 'suspend' && 'Suspend'}
              {confirmAction?.type === 'activate' && 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete — TerminateTenantControl's confirmation (name + reason), Owner only. */}
      <ConfirmTerminateDialog
        open={!!deleteTarget}
        tenantName={deleteTarget?.tenant_name || deleteTarget?.company_name}
        title={`Delete ${deleteTarget?.tenant_name || deleteTarget?.company_name || 'tenant'}?`}
        description={'Deleting terminates the tenant and removes it from this list: its users '
          + 'lose access, and it cannot be reactivated by an ordinary operation. Its data is '
          + 'kept. Suspend instead if you may need it back.'}
        submitLabel="Delete tenant"
        onTerminate={deleteTenant}
        onClose={() => setDeleteTarget(null)}
        returnFocusTo={deleteReturnFocus}
      />

      {/* Open Workspace — the operator's reason, then startViewAs. */}
      <ImpersonationReasonDialog
        target={viewAsTarget}
        post={postSupportAction}
        onClose={() => setViewAsTarget(null)}
      />
    </div>
  );
};

export default TenantsPage;
